// Bootstrap only. The loaded custom element remains the sole Cart state owner.
(() => {
  if(window.AplovioCartEntry)return;
  let promise,pending=false;
  const cart=()=>document.querySelector('aplovio-cart');
  const ready=()=>!!cart()?.hasAttribute('data-cart-ready');
  const section=async owner=>{
    const url=new URL(owner.dataset.cartUrl,location.href);
    if(url.origin!==location.origin)throw new Error('Invalid cart URL');
    url.searchParams.set('sections',owner.dataset.sectionId);
    const controller=new AbortController();
    const timer=setTimeout(()=>controller.abort(),12000);
    try{
      const response=await window.AplovioLazy.read(url,{credentials:'same-origin',headers:{Accept:'text/html'},signal:controller.signal});
      if(!response.ok)throw new Error('Cart section unavailable');
      const payload=await response.json(),html=payload?.[owner.dataset.sectionId];
      if(typeof html!=='string'||html.length>1000000)throw new Error('Invalid cart section');
      const doc=new DOMParser().parseFromString(html,'text/html');
      const fresh=[...doc.querySelectorAll('aplovio-cart')].find(node=>node.dataset.sectionId===owner.dataset.sectionId);
      if(!fresh||fresh.hasAttribute('data-cart-lazy')||!fresh.querySelector('[data-cart-content][data-item-count]')||!fresh.querySelector('[data-cart-footer]'))throw new Error('Incomplete cart section');
      return html;
    }finally{clearTimeout(timer);}
  };
  const state=(owner,value)=>{
    const shell=owner.querySelector('[data-cart-bootstrap]');
    if(!shell)return;
    const restoreFocus=value!=='error'&&shell.contains(document.activeElement);
    shell.hidden=value==='ready';
    shell.querySelector('[data-cart-bootstrap-progress]').hidden=value==='error';
    shell.querySelector('[data-cart-bootstrap-error]').hidden=value!=='error';
    owner.querySelector('dialog').setAttribute('aria-busy',String(value==='loading'));
    if(restoreFocus)owner.querySelector('[data-layer-close]').focus({preventScroll:true});
  };
  const load=()=>{
    if(ready())return Promise.resolve(cart());
    if(promise)return promise;
    const owner=cart();
    if(!owner)return Promise.reject(new Error('No cart'));
    state(owner,'loading');
    promise=(async()=>{
      const lazy=owner.hasAttribute('data-cart-lazy');
      const [html]=await Promise.all([
        lazy?section(owner):null,
        window.AplovioLazy.asset(owner.dataset.runtimeUrl,'script','aplovio-cart'),
        window.AplovioLazy.asset(owner.dataset.cssUrl,'style'),
        window.AplovioLazy.asset(owner.dataset.quantityUrl,'script','aplovio-quantity'),
        window.AplovioLazy.asset(owner.dataset.quantityCssUrl,'style')
      ]);
      if(!owner.isConnected||cart()!==owner||!owner.layer)throw new Error('Cart did not initialize');
      if(lazy){
        if(!owner.apply(html,owner.version))throw new Error('Cart changed during loading');
        if(owner.querySelector('[data-cart-kind=discount],[data-cart-kind=note]'))await owner.readState();
      }
      owner.setAttribute('data-cart-ready','');
      state(owner,'ready');
      if(owner.dialog.open&&owner.dialog.dataset.layerPhase!=='closing'){
        if(owner.querySelector('[data-cart-bootstrap]')?.contains(document.activeElement))owner.dialog.querySelector('[data-layer-close]').focus({preventScroll:true});
        window.AplovioLayers.animateContent(owner.dialog,owner.querySelector('[data-cart-content]'));
      }
      return owner;
    })().catch(error=>{state(owner,'error');throw error;}).finally(()=>{promise=null;});
    return promise;
  };
  window.AplovioCartEntry={load,submit};
  let opening=0;
  const openShell=async(owner,trigger)=>{
    const token=++opening,route=location.href,layer=owner.querySelector('aplovio-layer');
    const top=window.AplovioLayers.top();
    if(top&&top!==layer.dialog&&!top.matches('.ap-mobile-product-screen')){
      const closed=await new Promise(resolve=>{
        const finish=value=>{clearTimeout(timer);document.removeEventListener('aplovio:layer-close',onClose);resolve(value);};
        const onClose=event=>{if(event.target===top)finish(true);};
        const timer=setTimeout(()=>finish(false),4000);
        document.addEventListener('aplovio:layer-close',onClose);
        window.AplovioLayers.close(top,'cart');
      });
      if(!closed||token!==opening||location.href!==route||!trigger.isConnected||window.AplovioLayers.top())return;
    }
    layer.open(trigger.closest('dialog')&&!trigger.closest('.ap-mobile-product-screen')?document.querySelector('[data-cart-open]'):trigger);
  };
  document.addEventListener('click',async event=>{
    const trigger=event.target.closest('[data-cart-open]');
    const retry=event.target.closest('[data-cart-bootstrap-retry]');
    if((!trigger&&!retry)||event.defaultPrevented||event.button!==0||event.metaKey||event.ctrlKey||event.shiftKey||event.altKey||ready())return;
    const owner=cart(),layer=owner?.querySelector('aplovio-layer');
    if(!layer?.open)return;
    event.preventDefault();
    const opened=trigger?openShell(owner,trigger):null;
    // Completion updates the existing shell; it must never reopen a dismissed bag.
    try{await Promise.all([opened,load()]);}catch{ /* The shell retains retry and the native cart link. */ }
  });
  const signature=body=>JSON.stringify([...body].map(([name,value])=>[name,typeof value==='string'?value:[value.name,value.size,value.type,value.lastModified]]));
  // Called by the canonical form before Shopify's form-tracking listeners.
  // A document-level handler runs too late and can count both form and Ajax adds.
  async function submit(event,product){
    if(event.defaultPrevented||event.submitter?.hasAttribute('data-cart-native-fallback')&&!window.AplovioApps?.needsPurchasePreparation(event.target))return;
    const form=event.target,owner=cart();
    if(!owner||!product||product.nativeForm!==form||form.querySelector('[data-dynamic-checkout]')?.contains(event.submitter))return;
    event.preventDefault();if(!HTMLFormElement.prototype.checkValidity.call(form)||!product.selectedAvailable(product.variant.selectedOptions[0]))return;
    if(pending||owner.pending)return;
    const trigger=event.submitter||document.activeElement;
    const epoch=product.selectionEpoch,route=location.pathname+location.search,top=window.AplovioLayers?.top(),feedback=product.nextCartFeedback;
    // Cancel native tracking now, but capture fields after the whole submit dispatch.
    // A task boundary also covers native clicks where microtasks run between listeners.
    await new Promise(resolve=>setTimeout(resolve,0));
    if(pending||owner.pending||!product.isConnected||cart()!==owner||product.nativeForm!==form||epoch!==product.selectionEpoch||route!==location.pathname+location.search||window.AplovioLayers?.top()!==top||feedback&&(!feedback.layer.dialog.open||feedback.lifetime!==product.sizeLifetime))return;
    if(!HTMLFormElement.prototype.checkValidity.call(form)){HTMLFormElement.prototype.reportValidity.call(form);return;}
    if(!product.selectedAvailable(product.variant.selectedOptions[0]))return;
    const preparation=!!window.AplovioApps?.needsPurchasePreparation(form);
    if(ready()&&!preparation)return owner.add(product,trigger,undefined,feedback);
    const originalBody=new FormData(form),fingerprint=signature(originalBody),cartVersion=Number(owner.version||0);
    const report=(message,fallback=false,state='complete')=>{
      const node=form.querySelector('[data-cart-add-status]');if(!node)return;
      node.replaceChildren(document.createTextNode(message));node.hidden=false;
      if(fallback){const button=document.createElement('button');button.type='submit';button.className='ap-button ap-button--text';button.dataset.cartNativeFallback='';button.setAttribute('form',form.getAttribute('id'));button.textContent=owner.dataset.nativeAdd;const link=document.createElement('a');link.href=owner.dataset.cartUrl;link.textContent=owner.dataset.view;node.append(' ',button,' ',link);}
      product.sizeAddFeedback?.(feedback,node,state);
    };
    const busy=value=>{product.cartPending=value;form.setAttribute('aria-busy',String(value));product.updatePurchase(product.variant.selectedOptions[0]);product.updateSize();};
    const preparationAbort=new AbortController();
    const cancelPreparation=event=>{if(event.target===top)preparationAbort.abort();};
    document.addEventListener('aplovio:layer-beforeclose',cancelPreparation);
    window.addEventListener('pagehide',()=>preparationAbort.abort(),{once:true,signal:preparationAbort.signal});
    pending=true;product.appPreparing=preparation;product.purchaseFeedback('adding');busy(true);report(owner.dataset.addLoading||owner.dataset.runtimeLoading,false,'adding');
    try{
      const body=preparation?await window.AplovioApps.preparePurchase(product,{signal:preparationAbort.signal}):originalBody;
      const runtime=await load();
      if(preparationAbort.signal.aborted||!product.isConnected||product.nativeForm!==form||epoch!==product.selectionEpoch||route!==location.pathname+location.search||fingerprint!==signature(new FormData(form))||window.AplovioLayers?.top()!==top||preparation&&owner.version!==cartVersion||feedback&&(!feedback.layer.dialog.open||feedback.lifetime!==product.sizeLifetime)){
        report(owner.dataset.selectionChanged);return;
      }
      product.appPreparing=false;busy(false);pending=false;await runtime.add(product,trigger,body,feedback);
    }catch(error){
      if(preparation)report(error.shopperMessage||document.querySelector('[data-app-flow-strings]')?.dataset.purchaseError||owner.dataset.selectionChanged);
      else report(owner.dataset.loadError,true);
    }
    finally{
      document.removeEventListener('aplovio:layer-beforeclose',cancelPreparation);
      preparationAbort.abort();product.appPreparing=false;
      pending=false;if(product.isConnected&&!cart()?.pending){if(product.purchaseResult?.state==='adding')product.purchaseFeedback(null);busy(false);}
      if(product.nextCartFeedback===feedback){product.nextCartFeedback=null;product.finishStickyPurchase?.(feedback);}
    }
  }
})();
